import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/account/sync-subscribers")({
  head: () => ({
    meta: [
      { title: "Sync Subscribers to Shopify, HOWDEN" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: SyncSubscribersPage,
});

interface SyncResult {
  total: number;
  created: number;
  alreadyExists: number;
  failed: number;
  errors: Array<{ email: string; message: string }>;
}

function SyncSubscribersPage() {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runSync = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch("/api/public/sync-newsletter-to-shopify", {
        method: "POST",
        headers: { Authorization: `Bearer ${token.trim()}` },
      });
      if (!res.ok) {
        setError(`Request failed (${res.status}). Check your admin token.`);
        return;
      }
      const data = (await res.json()) as SyncResult;
      setResult(data);
    } catch (e: any) {
      setError(e?.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-20 lg:px-12">
      <p className="text-xs uppercase tracking-luxe text-muted-foreground">
        Admin Tool
      </p>
      <h1 className="mt-3 font-serif text-4xl font-light md:text-5xl">
        Sync Subscribers to Shopify
      </h1>
      <p className="mt-4 text-sm text-muted-foreground">
        Pushes every newsletter subscriber into Shopify as a customer with
        email-marketing consent. Existing customers are skipped.
      </p>

      <div className="mt-10 space-y-4 border border-hairline p-6">
        <label className="block text-xs uppercase tracking-luxe text-muted-foreground">
          Admin Token
        </label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Enter admin token"
          className="w-full border border-hairline bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-foreground"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          The token is sent only with this request and never stored.
        </p>
        <button
          onClick={runSync}
          disabled={!token.trim() || loading}
          className="border-b border-foreground pb-1 text-xs uppercase tracking-luxe disabled:opacity-40"
        >
          {loading ? "Syncing…" : "Run sync"}
        </button>
      </div>

      {error && (
        <div className="mt-8 border border-destructive/40 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-8 border border-hairline p-6 text-sm">
          <p className="font-serif text-xl">Sync complete</p>
          <ul className="mt-4 space-y-1 text-muted-foreground">
            <li>Total subscribers: {result.total}</li>
            <li>Created in Shopify: {result.created}</li>
            <li>Already existed: {result.alreadyExists}</li>
            <li>Failed: {result.failed}</li>
          </ul>
          {result.errors.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-luxe">Errors</p>
              <ul className="mt-2 max-h-64 space-y-1 overflow-auto text-xs text-muted-foreground">
                {result.errors.map((e, i) => (
                  <li key={i}>
                    <span className="font-mono">{e.email}</span>, {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
